import React, { useEffect, useState } from "react";
import { adminApi } from "../../api/admin";
import { formatApiError } from "../../lib/errors";

const EMPTY_FORM = {
  name: "",
  percent_off: "",
  amount_off: "",
  max_redemptions: "",
  expires_at: "",
  restricted_email: "",
};

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const fetchCoupons = () => { adminApi.getCoupons().then((resp) => setCoupons(resp.data)); };
  useEffect(() => { fetchCoupons(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setError("");
    try {
      const data = {
        name: form.name,
        percent_off: form.percent_off ? parseInt(form.percent_off) : null,
        amount_off: form.amount_off ? parseInt(form.amount_off) : null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        expires_at: form.expires_at || null,
        restricted_email: form.restricted_email || null,
      };
      await adminApi.createCoupon(data);
      setShowForm(false); setForm(EMPTY_FORM); fetchCoupons();
    } catch (err) { setError(formatApiError(err, "Failed to create coupon")); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this coupon?")) return;
    await adminApi.deleteCoupon(id); fetchCoupons();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-clash text-2xl font-bold text-white">Discount Codes</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary px-6 py-2 rounded text-sm font-semibold">{showForm ? "Cancel" : "New Coupon"}</button>
      </div>
      {showForm && (
        <div className="feature-card rounded-lg p-6 mb-6">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded mb-4 text-sm">{error}</div>}
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Code Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" required placeholder="TRAPMAN20" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Percent Off</label>
              <input type="number" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value, amount_off: "" })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" placeholder="20" min="1" max="100" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Max Redemptions</label>
              <input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" placeholder="Unlimited" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Expires At</label>
              <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Restricted to email</label>
              <input type="email" value={form.restricted_email} onChange={(e) => setForm({ ...form, restricted_email: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" placeholder="leave blank for public" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" className="btn-primary px-8 py-2 rounded text-sm font-semibold">Create Coupon</button>
            </div>
          </form>
        </div>
      )}
      <div className="space-y-2">
        {coupons.map((c) => {
          // A coupon is only redeemable if at least one of its codes is still
          // active. The coupon's own `valid` flag stays true even after a code
          // hits its max_redemptions, so it must NOT drive the status badge.
          const redeemable = c.codes?.length ? c.codes.some((p) => p.active) : c.valid;
          return (
          <div key={c.id} className="feature-card rounded-lg px-6 py-4 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold">{c.name || c.id}</p>
              {/* break-words: a restricted email is one unbreakable token and
                  overflowed the card on mobile. */}
              <p className="text-xs text-[#A1A1AA] break-words">
                {c.percent_off ? `${c.percent_off}% off` : c.amount_off ? `$${(c.amount_off / 100).toFixed(0)} off` : ""} &middot; {c.times_redeemed} redeemed
                {c.codes?.map((p) => (
                  <span key={p.id} className={`ml-2 font-mono ${p.active ? "text-[#FF5500]" : "text-[#A1A1AA] line-through"}`}>
                    {p.code}
                    {p.max_redemptions != null && <span className="ml-1 no-underline">({p.times_redeemed}/{p.max_redemptions} used)</span>}
                    {!p.active && <span className="ml-1 no-underline">(spent)</span>}
                    {p.restricted_email && <span className="text-[#A1A1AA] ml-1 no-underline">→ {p.restricted_email}</span>}
                  </span>
                ))}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs ${redeemable ? "text-green-400" : "text-red-400"}`}>{redeemable ? "Active" : "Inactive"}</span>
              {c.valid && <button onClick={() => handleDelete(c.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
